import { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { authApi } from '../api/auth';
import { useAuthStore } from '../store/authStore';

export default function VerifyEmailCallbackPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [error, setError] = useState('');
  const fetchCurrentUser = useAuthStore((state) => state.fetchCurrentUser);
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent double execution in React StrictMode
      if (hasVerified.current) return;
      hasVerified.current = true;

      if (!token) {
        setStatus('error');
        setError(t('verification.callback.noToken'));
        return;
      }

      try {
        await authApi.verifyEmail(token);
        setStatus('success');
        // Refresh user data to update emailVerifiedAt
        await fetchCurrentUser();
        // Notify other tabs that email was verified
        const channel = new BroadcastChannel('email-verification');
        channel.postMessage({ type: 'verified' });
        channel.close();
      } catch (err: any) {
        setStatus('error');
        const message = err.response?.data?.message;
        if (message === 'invalid_or_expired_token') {
          setError(t('verification.callback.invalidToken'));
        } else {
          setError(message || t('verification.callback.error'));
        }
      }
    };

    verifyEmail();
  }, [token, t, fetchCurrentUser]);

  return (
    <div className="min-h-screen flex items-start sm:items-center justify-center bg-gradient-to-br from-dark-950 via-dark-900 to-dark-800 px-4 pt-16 sm:pt-0 pb-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-6 animate-fade-in">
          <Link to="/" className="inline-flex items-center justify-center mb-3 hover:opacity-80 transition-opacity">
            <img src="/landing/logo-light.svg" alt="Fonorex" className="h-16" />
          </Link>
        </div>

        <div className="backdrop-blur-xl bg-white/10 border border-white/20 rounded-3xl shadow-2xl p-8 animate-scale-in">
          <div className="text-center">
            {status === 'loading' && (
              <>
                <div className="w-20 h-20 bg-gradient-to-r from-primary-600/20 to-secondary-600/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-10 h-10 text-primary-400 animate-spin" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {t('verification.callback.verifying')}
                </h1>
                <p className="text-gray-400">
                  {t('verification.callback.pleaseWait')}
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {t('verification.callback.success')}
                </h1>
                <p className="text-gray-400 mb-6">
                  {t('verification.callback.successDescription')}
                </p>
                <button
                  onClick={() => navigate('/home')}
                  className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all shadow-lg hover:shadow-glow"
                >
                  {t('verification.callback.continue')}
                </button>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-10 h-10 text-red-400" />
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">
                  {t('verification.callback.failed')}
                </h1>
                <p className="text-gray-400 mb-6">
                  {error}
                </p>
                <Link
                  to="/verify-email"
                  className="block w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-bold py-4 px-4 rounded-xl transition-all shadow-lg hover:shadow-glow text-center"
                >
                  {t('verification.callback.tryAgain')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
